import { parseApplicationEnvelope } from './applicationEnvelope';
import { fetchIpfsJson } from './ipfs';
import {
  APP_ROLE,
  APP_STATUS,
  listRegistryApplications,
  type OnChainApplication,
} from './registryClient';

export interface PublicOfficer {
  applicationId: number;
  didId: number;
  wallet: string;
  country: string;
  uri: string;
}

export function countriesMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

type EnvelopeFetcher = (cidOrUri: string) => Promise<unknown>;

export async function officerFromApplication(
  app: OnChainApplication,
  fetchJson: EnvelopeFetcher = fetchIpfsJson
): Promise<PublicOfficer | null> {
  if (app.appRole !== APP_ROLE.officer || app.status !== APP_STATUS.approved) {
    return null;
  }
  if (!app.uri.startsWith('ipfs://')) return null;
  try {
    const raw = await fetchJson(app.uri);
    const envelope = parseApplicationEnvelope(raw);
    return {
      applicationId: app.id,
      didId: app.didId,
      wallet: app.applicant,
      country: envelope.public.country,
      uri: app.uri,
    };
  } catch {
    return {
      applicationId: app.id,
      didId: app.didId,
      wallet: app.applicant,
      country: '',
      uri: app.uri,
    };
  }
}

export function latestOfficerPerWallet(rows: PublicOfficer[]): PublicOfficer[] {
  const byWallet = new Map<string, PublicOfficer>();
  for (const row of rows) {
    const key = row.wallet.toLowerCase();
    const prev = byWallet.get(key);
    if (!prev || row.applicationId > prev.applicationId) byWallet.set(key, row);
  }
  return [...byWallet.values()];
}

export function officerHistoryForWallet(
  apps: OnChainApplication[],
  wallet: string
): OnChainApplication[] {
  const target = wallet.toLowerCase();
  return apps
    .filter(
      (app) =>
        app.appRole === APP_ROLE.officer && app.applicant.toLowerCase() === target
    )
    .sort((a, b) => b.id - a.id);
}

export function buildOfficerReapplyState(opts: {
  wallet: string;
  liveDid: number | null;
  registeredDid: number | null;
  apps: OnChainApplication[];
}): {
  isReapply: boolean;
  didChanged: boolean;
  history: OnChainApplication[];
  latest: OnChainApplication | null;
  previousDid: number | null;
} {
  const history = officerHistoryForWallet(opts.apps, opts.wallet);
  const latest = history[0] ?? null;
  const previousDid = latest?.didId ?? opts.registeredDid ?? null;
  const didChanged =
    opts.liveDid != null && previousDid != null && opts.liveDid !== previousDid;
  return {
    isReapply: history.length > 0 || opts.registeredDid != null,
    didChanged,
    history,
    latest,
    previousDid,
  };
}

export async function listApprovedOfficers(
  chainId: number,
  country?: string,
  opts?: {
    listApps?: typeof listRegistryApplications;
    fetchJson?: EnvelopeFetcher;
  }
): Promise<PublicOfficer[]> {
  const list = opts?.listApps ?? listRegistryApplications;
  const fetchJson = opts?.fetchJson ?? fetchIpfsJson;
  const apps = await list(chainId);
  const officers: PublicOfficer[] = [];
  for (const app of apps) {
    const row = await officerFromApplication(app, fetchJson);
    if (!row) continue;
    if (country && row.country && !countriesMatch(row.country, country)) continue;
    if (country && !row.country) continue;
    officers.push(row);
  }
  return latestOfficerPerWallet(officers);
}
